import { useEffect, useRef, useState } from 'react'
import { PhoneCallIcon, PhoneOffIcon, PhoneIncomingIcon, PhoneOutgoingIcon, VolumeXIcon, Volume2Icon, Mic, MicOff, UserIcon, Grid3X3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useTwilio } from '@/contexts/TwilioContext'

export function GlobalCallNotification() {
  const {
    callState,
    isIncoming,
    incomingFrom,
    incomingCallerName,
    activeCallContact,
    activeCallDirection,
    isMuted,
    callDuration,
    ringtoneMuted,
    answeredBy,
    otherActiveCalls,
    answerCall,
    declineCall,
    hangUp,
    toggleMute,
    toggleRingtoneMute,
    sendDigits,
  } = useTwilio()

  const [showDialpad, setShowDialpad] = useState(false)
  const [dtmfInput, setDtmfInput] = useState('')

  const handleDigitPress = (digit: string) => {
    sendDigits(digit)
    setDtmfInput(prev => prev + digit)
  }

  const dialpadKeys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#'],
  ]

  // Keep a ref so the ring pattern interval can read current mute state
  const ringtoneMutedRef = useRef(ringtoneMuted)
  ringtoneMutedRef.current = ringtoneMuted

  // Ref to control gain node from mute effect without tearing down audio
  const audioNodesRef = useRef<{ ctx: AudioContext; gain: GainNode; oscillator: OscillatorNode; interval: ReturnType<typeof setInterval> } | null>(null)

  // Check if user is already on a call (to auto-mute new incoming calls)
  const isAlreadyOnCall = callState === 'connected' && !isIncoming

  // Create/destroy ringtone audio tied to ringing state only (NOT ringtoneMuted)
  useEffect(() => {
    if (isIncoming && callState === 'ringing') {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()

      oscillator.connect(gain)
      gain.connect(ctx.destination)

      oscillator.type = 'sine'
      oscillator.frequency.value = 440
      // Auto-mute if already on another call OR manually muted
      const shouldMute = isAlreadyOnCall || ringtoneMutedRef.current
      gain.gain.value = shouldMute ? 0 : 0.3

      // Ring pattern: 1s on, 2s off — checks mute ref each cycle
      const ringPattern = () => {
        gain.gain.cancelScheduledValues(ctx.currentTime)
        const shouldMute = isAlreadyOnCall || ringtoneMutedRef.current
        if (shouldMute) {
          gain.gain.setValueAtTime(0, ctx.currentTime)
          return
        }
        gain.gain.setValueAtTime(0.3, ctx.currentTime)
        gain.gain.setValueAtTime(0, ctx.currentTime + 0.5)
        gain.gain.setValueAtTime(0.3, ctx.currentTime + 0.6)
        gain.gain.setValueAtTime(0, ctx.currentTime + 1.0)
      }

      oscillator.start()
      ringPattern()
      const interval = setInterval(ringPattern, 3000)

      audioNodesRef.current = { ctx, gain, oscillator, interval }

      return () => {
        clearInterval(interval)
        try { oscillator.stop() } catch {}
        try { ctx.close() } catch {}
        audioNodesRef.current = null
      }
    }
  }, [isIncoming, callState, isAlreadyOnCall])

  // Immediately mute/unmute when the button is pressed (no audio teardown)
  useEffect(() => {
    const nodes = audioNodesRef.current
    if (!nodes) return
    const { gain, ctx } = nodes
    gain.gain.cancelScheduledValues(ctx.currentTime)
    gain.gain.setValueAtTime(ringtoneMuted ? 0 : 0.3, ctx.currentTime)
  }, [ringtoneMuted])

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Don't show anything if idle
  const isVisible = callState !== 'idle'

  // Also show when someone else answered (even if our local call state goes idle)
  const showNotification = isVisible || (answeredBy !== null && callState === 'idle')

  if (!showNotification) return null

  // When someone else answered the call (and we didn't)
  if (answeredBy && callState !== 'connected') {
    return (
      <div className="fixed bottom-6 right-6 z-[9999] w-80 animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="rounded-xl bg-card border-2 border-blue-500/30 shadow-2xl shadow-blue-500/10 overflow-hidden">
          <div className="bg-blue-600 px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              <span className="text-sm font-medium">{answeredBy.userName} answered the call</span>
            </div>
          </div>
          <div className="px-4 py-3">
            {incomingFrom && (
              <p className="text-sm text-muted-foreground mb-2">From: {incomingFrom}</p>
            )}
            <p className="text-xs text-muted-foreground">
              The call is being handled by {answeredBy.userName}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Incoming call ringing
  if (isIncoming && callState === 'ringing') {
    return (
      <div className="fixed bottom-6 right-6 z-[9999] w-80 animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="rounded-xl bg-card border-2 border-amber-500/50 shadow-2xl shadow-amber-500/20 overflow-hidden">
          {/* Header */}
          <div className="bg-amber-600 px-4 py-3 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PhoneIncomingIcon className="h-5 w-5 animate-pulse" />
                <span className="font-semibold">Incoming Call</span>
                <Badge variant="secondary" className="ml-1 bg-white/20 text-white border-0">
                  Incoming
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={toggleRingtoneMute}
              >
                {(ringtoneMuted || isAlreadyOnCall) ? (
                  <VolumeXIcon className="h-4 w-4" />
                ) : (
                  <Volume2Icon className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 py-4 space-y-3">
            <div className="text-center">
              {activeCallContact ? (
                <>
                  <p className="font-semibold text-lg">{activeCallContact.name}</p>
                  <p className="text-sm text-muted-foreground">{incomingFrom}</p>
                  {activeCallContact.type && (
                    <Badge variant="outline" className="mt-1 text-xs">
                      {activeCallContact.type}
                    </Badge>
                  )}
                </>
              ) : incomingCallerName ? (
                <>
                  <p className="font-semibold text-lg">{incomingCallerName}</p>
                  <p className="text-sm text-muted-foreground">{incomingFrom}</p>
                </>
              ) : (
                <p className="font-medium text-lg">{incomingFrom || 'Unknown Caller'}</p>
              )}
              {(ringtoneMuted || isAlreadyOnCall) && (
                <p className="text-xs text-muted-foreground mt-1">
                  {isAlreadyOnCall ? 'Ringtone muted (already on call)' : 'Ringtone muted'}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={answerCall}
                className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white"
                size="lg"
              >
                <PhoneCallIcon className="h-5 w-5 mr-2" />
                Answer
              </Button>
              <Button
                onClick={declineCall}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white"
                size="lg"
              >
                <PhoneOffIcon className="h-5 w-5 mr-2" />
                Decline
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Active call (connecting, connected, ringing for outbound)
  if (callState === 'connecting' || callState === 'connected' || (callState === 'ringing' && !isIncoming)) {
    const directionIcon = activeCallDirection === 'incoming' ? PhoneIncomingIcon : PhoneOutgoingIcon
    const DirectionIcon = directionIcon

    return (
      <div className="fixed bottom-6 right-6 z-[9999] w-80 animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="rounded-xl bg-card border-2 border-green-500/30 shadow-2xl shadow-green-500/10 overflow-hidden">
          {/* Header */}
          <div className={`px-4 py-3 text-white ${callState === 'connected' ? 'bg-green-600' : 'bg-blue-600'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PhoneCallIcon className="h-4 w-4 animate-pulse" />
                <span className="text-sm font-medium">
                  {callState === 'connecting' ? 'Connecting...' : callState === 'ringing' ? 'Ringing...' : 'On Call'}
                </span>
                {activeCallDirection && (
                  <Badge variant="secondary" className="ml-1 bg-white/20 text-white border-0 text-xs">
                    <DirectionIcon className="h-3 w-3 mr-1" />
                    {activeCallDirection === 'incoming' ? 'Incoming' : 'Outgoing'}
                  </Badge>
                )}
              </div>
              {callState === 'connected' && (
                <span className="text-sm font-mono">{formatDuration(callDuration)}</span>
              )}
            </div>
          </div>

          {/* Body - Contact info */}
          <div className="px-4 py-3 border-b">
            <div className="text-center">
              {activeCallContact ? (
                <>
                  <p className="font-semibold text-base">{activeCallContact.name}</p>
                  <p className="text-xs text-muted-foreground">{incomingFrom}</p>
                  {activeCallContact.type && (
                    <Badge variant="outline" className="mt-1 text-xs">
                      {activeCallContact.type}
                    </Badge>
                  )}
                </>
              ) : incomingCallerName ? (
                <>
                  <p className="font-semibold text-base">{incomingCallerName}</p>
                  <p className="text-xs text-muted-foreground">{incomingFrom}</p>
                </>
              ) : (
                <p className="text-sm font-medium">{incomingFrom || 'Unknown'}</p>
              )}
            </div>
          </div>

          {/* DTMF Dial Pad */}
          {showDialpad && callState === 'connected' && (
            <div className="px-4 py-3 border-b space-y-2">
              {dtmfInput && (
                <div className="text-center text-base font-mono tracking-widest text-foreground">
                  {dtmfInput}
                </div>
              )}
              <div className="grid grid-cols-3 gap-1.5">
                {dialpadKeys.map((row) =>
                  row.map((digit) => (
                    <Button
                      key={digit}
                      variant="outline"
                      className="h-10 text-base font-semibold"
                      onClick={() => handleDigitPress(digit)}
                    >
                      {digit}
                    </Button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-center gap-2">
              {callState === 'connected' && (
                <>
                  <Button
                    variant={isMuted ? 'default' : 'outline'}
                    size="icon"
                    className="h-10 w-10 rounded-full"
                    onClick={toggleMute}
                  >
                    {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant={showDialpad ? 'default' : 'outline'}
                    size="icon"
                    className="h-10 w-10 rounded-full"
                    onClick={() => { setShowDialpad(!showDialpad); setDtmfInput('') }}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button
                onClick={hangUp}
                className="h-10 px-6 bg-red-600 hover:bg-red-700 text-white rounded-full"
              >
                <PhoneOffIcon className="h-4 w-4 mr-2" />
                Hang Up
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Disconnected state (brief)
  if (callState === 'disconnected') {
    return (
      <div className="fixed bottom-6 right-6 z-[9999] w-80 animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="rounded-xl bg-card border-2 border-muted shadow-lg overflow-hidden">
          <div className="bg-muted px-4 py-3">
            <div className="flex items-center gap-2">
              <PhoneOffIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Call Ended</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show other users' active calls (when user is idle or on a call)
  if (otherActiveCalls.length > 0) {
    return (
      <div className="fixed bottom-6 right-6 z-[9998] space-y-2">
        {otherActiveCalls.map((activeCall) => {
          const DirectionIcon = activeCall.direction === 'incoming' ? PhoneIncomingIcon : PhoneOutgoingIcon
          return (
            <div
              key={activeCall.callSid}
              className="w-80 animate-in slide-in-from-bottom-4 fade-in duration-300"
            >
              <div className="rounded-xl bg-card border-2 border-blue-500/30 shadow-xl overflow-hidden">
                <div className="bg-blue-600 px-4 py-3 text-white">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">{activeCall.userName} is on a call</span>
                    <Badge variant="secondary" className="ml-auto bg-white/20 text-white border-0 text-xs">
                      <DirectionIcon className="h-3 w-3 mr-1" />
                      {activeCall.direction === 'incoming' ? 'Incoming' : 'Outgoing'}
                    </Badge>
                  </div>
                </div>
                <div className="px-4 py-3">
                  {activeCall.contactName ? (
                    <>
                      <p className="text-sm font-semibold">{activeCall.contactName}</p>
                      <p className="text-xs text-muted-foreground">{activeCall.phoneNumber}</p>
                    </>
                  ) : (
                    <p className="text-sm">{activeCall.phoneNumber || 'Unknown'}</p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return null
}
